import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNewScanWizard } from "../NewScanWizardProvider";
import { fetchToolCatalog, fetchScanProfiles } from "../../../api/scans";

const ConfigureStep = () => {
  const navigate = useNavigate();
  const { selectedToolIds, config, setConfig } = useNewScanWizard();
  const [tools, setTools] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const hasNmapTool = selectedToolIds.some(id => id.startsWith("nmap_"));

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const toolData = await fetchToolCatalog(false);
        setTools(toolData);

        if (hasNmapTool) {
          const profileData = await fetchScanProfiles();
          setProfiles(profileData);
        }
      } catch (err) {
        setError("Failed to load configuration data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [hasNmapTool]);

  const handleNmapProfileChange = (e) => {
    setConfig(prev => ({
      ...prev,
      nmap_profile: e.target.value,
    }));
  };

  const handleNext = () => {
    navigate("/scans/new/review");
  };

  if (loading) {
    return <div className="step-content-loading">Loading configuration options...</div>;
  }

  const selectedTools = tools.filter(t => selectedToolIds.includes(t.id));

  return (
    <div className="configure-step">
      <h2>Step 3: Configure Scan</h2>
      <p className="step-description">
        Configure advanced options for your scan.
      </p>

      {error && <div className="error-banner">{error}</div>}

      <div className="config-sections">
        {/* Nmap Profile Selection */}
        {hasNmapTool && profiles.length > 0 && (
          <div className="config-section">
            <h3>Nmap Profile</h3>
            <p className="section-description">
              Select the scan profile for Nmap tools.
            </p>
            <div className="profile-options">
              {profiles.map(profile => (
                <label key={profile.id} className="profile-option">
                  <input
                    type="radio"
                    name="nmap_profile"
                    value={profile.id}
                    checked={config.nmap_profile === profile.id || (!config.nmap_profile && profile.id === "service")}
                    onChange={handleNmapProfileChange}
                  />
                  <div>
                    <strong>{profile.name}</strong>
                    <p className="profile-desc">{profile.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Selected Tools Summary */}
        <div className="config-section">
          <h3>Selected Scan Tools Summary</h3>
          <p className="section-description">
            The following tools will be executed against your targets:
          </p>
          <div className="tools-summary">
            {selectedTools.map(tool => (
              <div key={tool.id} className="tool-summary-item">
                <h4>{tool.name}</h4>
                <p className="tool-category">{tool.category}</p>
                <p className="tool-desc">{tool.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Disabled Features */}
        <div className="config-section disabled-section">
          <h3>Advanced Options (Coming Soon)</h3>
          <div className="disabled-feature">
            <h4>Notifications</h4>
            <p>Email and Slack notifications will be available in a future release.</p>
          </div>
          <div className="disabled-feature">
            <h4>Scheduling</h4>
            <p>Recurring scans and scheduled execution will be available in a future release.</p>
          </div>
        </div>
      </div>

      <div className="step-actions">
        <button type="button" className="primary-button" onClick={handleNext}>
          Next →
        </button>
      </div>
    </div>
  );
};

export default ConfigureStep;
